import { Divider, Headline, IconButton, Text, TextButton, TextLink } from '@oneaudi/unified-web-components';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { RegisterOrUpdateConsumptionFootnoteForDialog } from '~/app/compositions/content/calculation-info/components/consumption-footnote';
import { FsagMonthlyPriceRateDialog } from '~/app/compositions/content/calculation-info/components/fsag-finance-dialog';

import {
	ButtonsStyle,
	HeaderStyle,
	OverviewStyle,
	PriceDetailsStyle,
	SubtotalStyles,
} from '~/app/compositions/content/service-footer/components/price-details/price-details-style';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { i18nKeys, useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { StockCarEnabled, useStockCarDialog } from '~/app/pages/configurator/components/dialogs/stock-car-dialog';
import { useCtxConfigurator } from '~/app/pages/configurator/context/ctx-configurator';
import { usePriceDetailRemarks } from '~/app/pages/summary/dialogs/price-detail';
import { PriceSection } from '~/app/pages/summary/dialogs/price-detail/components/price-section';
import { RateSection } from '~/app/pages/summary/dialogs/price-detail/components/rate-section';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useIframeDialog } from '~/components/dialog/iframe-dialog/iframe-dialog';
import { FootnoteRenderer, useFootnotes } from '~/components/footnotes';
import { CAE_CONFIGUREDCAR_REMARK } from '~/components/footnotes/constants';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';
import { RenderHtml } from '~/components/text';
import { TooltipLabel } from '~/components/tooltip-label/tooltip-label';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCarlineQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { state } from '~/storage';
import { DATA_TEST_ID } from '~/test/data-test-id';
import { sortIncentives } from '~/utils/incentives';
import { getFilteredPriceParts } from '~/app/utils/FilteredPriceParts';

export const PriceDetails = () => {
	const { setOpenPriceDetails } = useCtxConfigurator();
	const { i18n } = useI18n();

	return (
		<PriceDetailsStyle id={'price-details'}>
			<div className="close-button">
				<IconButton
					variant={'tertiary'}
					icon={'arrow-down'}
					size={'md'}
					onClick={() => setOpenPriceDetails(false)}
					aria-label={i18n('ngc.actions.showPriceDetails')}
				/>
			</div>
			<div className="content">
				<Header />
				<Overview />
				<FootnoteRenderer />
				<Buttons />
			</div>
		</PriceDetailsStyle>
	);
};

const Header = () => {
	const { config } = useMarketConfig();
	const { catalog } = useConfigurator();
	const { getReference } = useFootnotes();

	if (config.showCaESummaryText && !config.hideConsumptionEmissionEngineName) {
		RegisterOrUpdateConsumptionFootnoteForDialog();
	}

	return (
		<HeaderStyle>
			<Headline tag="h2" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
				{catalog.carInfo?.carline}
			</Headline>
			<Text tag="p" variant={{ type: 'copy', order: '1', style: 'normal' }}>
				{catalog.carInfo?.model}
				{!config.hideConsumptionEmissionEngineName && (
					<FootnoteReferenceNgc reference={getReference(CAE_CONFIGUREDCAR_REMARK)} />
				)}
			</Text>
		</HeaderStyle>
	);
};

const Overview = () => {
	const { i18n } = useI18n();
	const { config, formatter } = useMarketConfig();
	const { priceSummary } = useCatalog();
	const { formatCurrency, formatMonthlyRate } = useCurrencyFormatter();
	const vatConfig = useVATConfig();
	const { catalog, carIdentifier } = useConfigurator();
	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);
	const financialProducts = useAtomValue(state.financialProducts);
	const { openDialog, isDialogOpen, closeDialog } = useDialogs();
	const { showIframeDialog } = useIframeDialog();
	const selectedProductSettings = useAtomValue(state.fsagFinancialCalculatorSettings);
	const defaultProduct = useAtomValue(state.defaultFinancialProduct);
	const { priceDateRemark } = usePriceDetailRemarks(config.useStaticPriceDateRemark);
	const carType = carlineData?.carline?.configuredCar?.carlineStructureCarline?.vehicleType;

	const priceParts = getFilteredPriceParts(priceSummary, config);

	const priceDisclaimerText = useMemo(() => {
		if (config.enablePriceDisclaimer) {
			return carType
				? carType === 'BEV'
					? i18n('ngc.remarks.price.legalBEV')
					: i18n('ngc.remarks.price.legalICE')
				: undefined;
		}
		return null;
	}, [config.enablePriceDisclaimer, carType, i18n]);

	const product = selectedProductSettings ? (selectedProductSettings['@ID'] ?? '') : (defaultProduct ?? '');
	const incentives =
		config.enableIncentives && carlineData.carline?.configuredCar.incentives
			? sortIncentives(carlineData.carline?.configuredCar.incentives)
			: [];

	const selectedIncentives = incentives.filter((incentive) => incentive.status.selected);

	return (
		<OverviewStyle>
			<Headline tag="h2" variant={{ type: 'headline', order: '4', weight: 'bold' }}>
				{i18n('ngc.priceDetails.title')}
			</Headline>
			<div className="prices">
				{priceParts?.map((part) => (
					<PriceSection
						key={part.type}
						pricePartType={part.type}
						title={i18n(`ngc.priceDetails.${part.type}` as keyof typeof i18nKeys)}
						price={formatCurrency(part.price, vatConfig.priceParts[part.type])}
						disclaimer={null}
					/>
				))}
			</div>
			<Divider />
			{config.enableIncentives && selectedIncentives.length > 0 && (
				<>
					<SubtotalStyles>
						<PriceSection
							pricePartType="subtotal"
							title={
								<TooltipLabel
									label={i18n('ngc.currency.subtotal')}
									tooltip={i18n('ngc.priceDetails.subtotal.tooltip')}
									variant={{ type: 'copy', order: '2', style: 'normal' }}
									tooltipSize={'xs'}
									popoverAnchor={'price-details'}
								/>
							}
							price={formatCurrency(
								priceSummary?.parts?.find((part) => part.type === 'totalWithoutIncentives')?.price,
								vatConfig.totalPrice,
							)}
							disclaimer={null}
						/>
						{selectedIncentives.map((incentive) => {
							return (
								<PriceSection
									key={incentive.id}
									pricePartType="incentive"
									title={incentive.action.title}
									price={formatter.formatPrice(incentive.price.value)}
									disclaimer={null}
								/>
							);
						})}
					</SubtotalStyles>
					<Divider />
				</>
			)}
			<PriceSection
				pricePartType="total"
				title={i18n('ngc.priceDetails.total')}
				price={formatCurrency(catalog.priceSummary?.totalPrice, vatConfig.totalPrice)}
				bold
				disclaimer={priceDisclaimerText}
			/>
			{catalog?.priceSummary?.monthlyRate?.__typename === 'MonthlyRate' && config.enableFinancingServices && (
				<div className="rates">
					<RateSection
						pricePartType="totalRate"
						title={
							financialProducts.find((prod) => {
								return prod.key === product;
							})?.val ?? i18n('ngc.priceDetails.privateLeasing')
						}
						price={formatMonthlyRate(catalog.priceSummary?.monthlyRate)}
						bold
					/>
					<TextLink
						variant="standalone"
						size="sm"
						onClick={() => {
							appTrackingService.summaryClickEvent({
								eventAction: EventAction.InternalLink,
								eventName: `configurator (price details) - click to open configurator-financing-options-layer`,
								componentName: 'price-details-layer',
								label: i18n('ngc.actions.changeLeasingRate'),
							});
							openDialog('monthlyPriceRatesFsag');
						}}>
						{i18n('ngc.actions.changeLeasingRate')}
					</TextLink>
					{config.deliveryCostUrl && (
						<TextLink
							variant="standalone"
							onClick={() => {
								showIframeDialog({ url: config.deliveryCostUrl, name: 'delivery-cost-layer' });
							}}
							size="sm">
							{i18n('ngc.priceDetails.deliveryCosts')}
						</TextLink>
					)}
				</div>
			)}
			{priceDateRemark && (
				<Text tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }}>
					<RenderHtml html={priceDateRemark} />
				</Text>
			)}
			{catalog?.priceSummary?.monthlyRate?.__typename === 'MonthlyRate' && config.enableFinancingServices && (
				<div>
					<FsagMonthlyPriceRateDialog
						open={isDialogOpen('monthlyPriceRatesFsag')}
						onClose={() => closeDialog('monthlyPriceRatesFsag')}
					/>
				</div>
			)}
		</OverviewStyle>
	);
};

const Buttons = () => {
	const [, setCurrentView] = useAtom(state.currentView);
	const { i18n } = useI18n();
	const { config } = useMarketConfig();
	const { showStockCarDialog } = useStockCarDialog(config.stockCarConfig.geoFilter);
	const { setOpenPriceDetails } = useCtxConfigurator();

	const handleClickSummary = useCallback(() => {
		const body = document?.getElementsByTagName('body');
		body[0].style.overflow = 'auto';
		setOpenPriceDetails(false);
		setCurrentView('summary');
		appTrackingService.basketNavigationClick({
			label: i18n('ngc.actions.getYourCar'),
			targetUrl: '#summary',
			targetPage: 'summary',
		});
	}, [i18n, setCurrentView]);

	const handleClickStockCar = () => {
		setOpenPriceDetails(false);
		showStockCarDialog();
	};

	return (
		<ButtonsStyle>
			{StockCarEnabled() && (
				<TextButton
					className="main-cta-button"
					type={'button'}
					variant={'secondary'}
					onClick={handleClickStockCar}
					size={'sm'}
					isFullWidth
					data-testid={DATA_TEST_ID.STOCK_CAR_BUTTON}>
					{i18n('ngc.stockcar.trigger')}
				</TextButton>
			)}
			<TextButton
				href={'#summary'}
				variant="primary"
				onClick={handleClickSummary}
				size="sm"
				data-testid={DATA_TEST_ID.SUMMARY_BUTTON}>
				{i18n('ngc.actions.getYourCar')}
			</TextButton>
		</ButtonsStyle>
	);
};
